import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import FrontechLogo from './FrontechLogo';
import { Eye, EyeOff, ArrowRight, Sun, Moon, Mic, Globe, BarChart3 } from 'lucide-react';

const BRAND_POINTS = [
  { icon: Mic,       text: 'Natural voice conversations in real-time' },
  { icon: Globe,     text: 'Deploy to any website in seconds' },
  { icon: BarChart3, text: 'Insights and analytics out of the box' },
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (err.message || 'Login failed'));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--color-base)' }}>

      {/* ── Left brand panel (hidden on mobile) ── */}
      <div className="hidden lg:flex lg:w-[46%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'var(--color-surface)', borderRight: '1px solid var(--color-border)' }}>

        {/* Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ opacity: 'var(--watermark-opacity)' }}>
          <FrontechLogo size={500} variant="watermark" color={`rgb(var(--watermark-color))`} />
        </div>

        {/* Gradient blob */}
        <div className="absolute bottom-0 left-0 w-96 h-96 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,214,126,0.06) 0%, transparent 70%)', filter: 'blur(60px)' }} />

        {/* Top logo */}
        <div className="relative z-10 flex items-center gap-2.5">
          <FrontechLogo size={28} variant="icon" color="var(--color-accent)" />
          <span className="font-heading font-semibold text-base" style={{ color: 'var(--color-text-primary)' }}>Front</span>
          <span className="text-xs font-mono" style={{ color: 'var(--color-text-muted)' }}>by FRONTECH</span>
        </div>

        {/* Center content */}
        <div className="relative z-10">
          <FrontechLogo size={80} variant="animated" color="var(--color-accent)" className="mb-8" />
          <h2 className="font-heading text-3xl font-bold tracking-tight leading-tight mb-4"
            style={{ color: 'var(--color-text-primary)' }}>
            Voice AI that works<br />
            <span className="text-gradient-accent">while you sleep</span>
          </h2>
          <p className="text-sm leading-relaxed mb-8" style={{ color: 'var(--color-text-secondary)' }}>
            Give every visitor on your website a personal AI guide that knows your business inside and out.
          </p>
          <div className="space-y-4">
            {BRAND_POINTS.map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: 'var(--color-accent-dim)' }}>
                  <Icon size={15} style={{ color: 'var(--color-accent)' }} />
                </div>
                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom quote */}
        <div className="relative z-10">
          <p className="text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
            "Built for businesses that want to move fast without breaking things."
          </p>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 py-4">
          <Link to="/" className="lg:hidden flex items-center gap-2">
            <FrontechLogo size={22} variant="icon" color="var(--color-accent)" />
            <span className="font-heading font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>Front</span>
          </Link>
          <div className="lg:hidden flex-1" />
          <button
            onClick={toggle}
            className="p-2 rounded-lg cursor-pointer transition-colors ml-auto"
            style={{ color: 'var(--color-text-secondary)', background: 'var(--color-elevated)' }}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <h1 data-testid="login-heading" className="font-heading text-2xl font-bold tracking-tight"
                style={{ color: 'var(--color-text-primary)' }}>
                Welcome back
              </h1>
              <p className="mt-1.5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Sign in to your FRONTECH account
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              {error && (
                <div data-testid="login-error"
                  className="text-sm rounded-xl px-4 py-3"
                  style={{
                    background: 'rgba(239,68,68,0.08)',
                    border: '1px solid rgba(239,68,68,0.2)',
                    color: '#f87171',
                  }}>
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  Email
                </label>
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="input-field focus-ring-accent"
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  Password
                </label>
                <div className="relative">
                  <input
                    data-testid="login-password-input"
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="input-field focus-ring-accent"
                    style={{ paddingRight: '44px' }}
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer transition-colors"
                    style={{ color: 'var(--color-text-muted)' }}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              <button
                data-testid="login-submit-btn"
                type="submit"
                disabled={loading}
                className="btn-accent w-full justify-center py-3.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading
                  ? <><span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />Signing in...</>
                  : <>Sign In <ArrowRight size={16} /></>
                }
              </button>
            </form>

            <p className="text-center text-sm mt-6" style={{ color: 'var(--color-text-muted)' }}>
              Don't have an account?{' '}
              <Link to="/signup" data-testid="login-to-signup-link"
                className="font-semibold transition-colors cursor-pointer"
                style={{ color: 'var(--color-accent)' }}>
                Create one free
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
